import axios, { AxiosError } from "axios";
import { plainToInstance } from "class-transformer";
import { IdentifiedPatientsResponseDto, StudyRecruitmentDto, } from "healthex-common/recruitment.dtos";
import { ApiResult, err } from "healthex-common/result";
import { StudySearchResult } from "healthex-common/study-with-includes.dtos";
import { ClinicalTrialsDotGovStudyDto, StudyConflictErrorDto, StudyDto, StudyICFTextSegmentDto, StudyReminderDto, StudyUserActivityDto, StudyUserDto, StudyVersionStatus, StudyVersionLiteDto, } from "healthex-common/study.dtos";
import { TranscodeConflictErrorDto } from "healthex-common/transcode.dtos";
import { apiConfig, authenticatedApiConfig, generateURL } from "./BaseApiService";
import { willNeverHappen } from "healthex-common/utils";
export class StudyService {
    constructor(authenticationProvider, environment) {
        this.authenticationProvider = authenticationProvider;
        this.environment = environment;
    }
    async getClinicalStudyForImport(nctId, orgId) {
        var _a;
        try {
            const response = await axios.post(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/draft/$import`, { orgId }), { nctId }, authenticatedApiConfig(this.authenticationProvider));
            return new ApiResult(plainToInstance(ClinicalTrialsDotGovStudyDto, response.data));
        }
        catch (e) {
            if (axios.isAxiosError(e) && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.data)) {
                const errorData = e.response.data;
                if (errorData.message && errorData.statusCode && errorData.statusCode === 400) {
                    return new ApiResult(null, [errorData.message.toString()]);
                }
            }
            return null;
        }
    }
    async getStudyById(id, studyVersionInput) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/studies/:id/:studyVersionIdOrType`, {
            id,
            studyVersionIdOrType: this.studyVersionInputToPathParams(studyVersionInput),
        }), authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(StudyDto, response.data);
    }
    async getStudyPublicInfoById(id, studyVersionInput) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/studies/:id/:studyVersionIdOrType/public`, {
            id,
            studyVersionIdOrType: this.studyVersionInputToPathParams(studyVersionInput),
        }), apiConfig());
        return plainToInstance(StudyDto, response.data);
    }
    async getExistingDiseaseAreas() {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/studies/disease-areas`), authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async postSearchForStudies(orgId, versionType, searchInput, start, end) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:studyVersionType/search`, {
            orgId,
            studyVersionType: this.studyVersionTypeToPathParams(versionType),
        }), searchInput, authenticatedApiConfig(this.authenticationProvider, { start, end }));
        return plainToInstance(StudySearchResult, response.data);
    }
    async postSearchForStudiesCount(versionType, searchInputs) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, `/studies/search/:studyVersionType/counts`, {
            studyVersionType: this.studyVersionTypeToPathParams(versionType),
        }), searchInputs, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async hasAtLeastOneStudy(orgId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/studies/:orgId/hasatleastone`, { orgId }), authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async updateStudyVersion(id, orgId, update) {
        var _a;
        try {
            const response = await axios.put(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:id/draft`, { orgId, id }), update, authenticatedApiConfig(this.authenticationProvider));
            return plainToInstance(StudyDto, response.data);
        }
        catch (e) {
            if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 409) {
                return err(plainToInstance(StudyConflictErrorDto, e.response.data));
            }
            else {
                throw e;
            }
        }
    }
    async updateStudy(id, orgId, update) {
        var _a;
        try {
            const response = await axios.put(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:id`, { orgId, id }), update, authenticatedApiConfig(this.authenticationProvider));
            return plainToInstance(StudyDto, response.data);
        }
        catch (e) {
            if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 409) {
                return err(plainToInstance(StudyConflictErrorDto, e.response.data));
            }
            else {
                throw e;
            }
        }
    }
    async createStudy(orgId, input) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/`, { orgId }), input, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getStudyDraftVersionNumber(id, orgId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:id/draft/version-number`, { orgId, id }), authenticatedApiConfig(this.authenticationProvider));
        return parseInt(response.data);
    }
    async createStudyDraftIfNecessary(id, orgId) {
        await axios.post(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:id/draft/$create-if-necessary`, { orgId, id }), {}, authenticatedApiConfig(this.authenticationProvider));
    }
    async deleteStudyDraft(id, orgId) {
        await axios.delete(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:id/draft`, { orgId, id }), authenticatedApiConfig(this.authenticationProvider));
    }
    async getRecruitment(orgId, studyId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:studyId/recruitment`, { orgId, studyId }), authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(StudyRecruitmentDto, response.data);
    }
    async getIdentifiedPatients(orgId, studyId, searchInput) {
        var _a, _b;
        const queryParams = {
            ...searchInput,
            patientInfo: (_a = searchInput.patientInfo) === null || _a === void 0 ? void 0 : _a.join(","),
            state: (_b = searchInput.state) === null || _b === void 0 ? void 0 : _b.join(","),
        };
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:studyId/recruitment/identified-patients`, {
            orgId,
            studyId,
        }), authenticatedApiConfig(this.authenticationProvider, queryParams));
        return plainToInstance(IdentifiedPatientsResponseDto, response.data);
    }
    async getIdentifiedPatientSummary(orgId, studyId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:studyId/recruitment/identified-patients/summary`, {
            orgId,
            studyId,
        }), authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async updateRecruitment(orgId, studyId, input) {
        const response = await axios.put(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:studyId/recruitment`, { orgId, studyId }), input, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async updateIdentifiedPatient(orgId, studyId, identifiedPatientId, input) {
        await axios.put(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:studyId/recruitment/identified-patients/:identifiedPatientId`, { orgId, studyId, identifiedPatientId }), input, authenticatedApiConfig(this.authenticationProvider));
    }
    async updateIdentifiedPatientByPatientId(orgId, studyId, patientId, input) {
        await axios.put(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:studyId/recruitment/identified-patients`, {
            orgId,
            studyId,
            patientId,
        }), input, authenticatedApiConfig(this.authenticationProvider, { patientId }));
    }
    async getOverviewStats(orgId, studyId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:studyId/overviewstats`, { orgId, studyId }), authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getWhatsNewLastSeen(orgId, studyId, userId) {
        try {
            const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:studyId/useractivity/:userId`, {
                orgId,
                studyId,
                userId,
            }), authenticatedApiConfig(this.authenticationProvider));
            return plainToInstance(StudyUserActivityDto, response.data);
        }
        catch (e) {
            console.log(e);
            return null;
        }
    }
    async updateWhatsNewLastSeen(orgId, studyId, userId) {
        try {
            const response = await axios.put(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:studyId/useractivity/:userId/updatelastseen`, {
                orgId,
                studyId,
                userId,
            }), {}, authenticatedApiConfig(this.authenticationProvider));
            return plainToInstance(StudyUserActivityDto, response.data);
        }
        catch (e) {
            console.log(e);
            return null;
        }
    }
    async getWhatsNew(orgId, studyId, since) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:studyId/whatsnew/:since`, {
            orgId,
            studyId,
            since: since !== null && since !== void 0 ? since : "",
        }), authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getHipaaAuthorizationFormUploadUrl(orgId, studyId, file) {
        const localFileInfo = { filename: file.name };
        const response = await axios.put(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:studyId/draft/hipaa-authorization-form-upload-url`, {
            orgId,
            studyId,
        }), localFileInfo, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async updateOrgLogoUrlForInformedConsent(orgId, studyId, file) {
        const localFileInfo = { filename: file.name };
        const response = await axios.put(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:studyId/draft/org-logo-url-for-informed-consent`, {
            orgId,
            studyId,
        }), localFileInfo, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getOrgIconUrl(orgId, studyId, file) {
        const localFileInfo = { filename: file.name };
        const response = await axios.put(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:studyId/draft/org-icon-url`, { orgId, studyId }), localFileInfo, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async generatePatientFacingDetails(orgId, studyId, input, field) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:studyId/draft/generatePatientDetails`, {
            orgId,
            studyId,
        }), { input, field }, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async translateStudyFields(orgId, studyId, input) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:studyId/draft/translate`, { orgId, studyId }), input, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async isAIGenerationRequired(orgId, studyId, input) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:studyId/draft/isAIGenerationRequired`, {
            orgId,
            studyId,
        }), { input }, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getStudyUsers(orgId, studyId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/studies/:studyId/users", { orgId, studyId }), authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(StudyUserDto, response.data);
    }
    async createOrUpdateStudyUser(orgId, studyId, userId, input) {
        await axios.put(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/studies/:studyId/users/:userId", {
            orgId,
            studyId,
            userId,
        }), input, authenticatedApiConfig(this.authenticationProvider));
    }
    async deleteStudyUser(orgId, studyId, userId) {
        await axios.delete(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/studies/:studyId/users/:userId", {
            orgId,
            studyId,
            userId,
        }), authenticatedApiConfig(this.authenticationProvider));
    }
    async getEnrollmentVideoUploadUrl(orgId, studyId, transcodeInput) {
        var _a;
        try {
            const response = await axios.put(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:studyId/draft/enrollment-video-upload-url`, {
                orgId,
                studyId,
            }), transcodeInput, authenticatedApiConfig(this.authenticationProvider));
            return response.data;
        }
        catch (e) {
            if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 409) {
                return err(plainToInstance(TranscodeConflictErrorDto, e.response.data));
            }
            else {
                throw e;
            }
        }
    }
    async updateStudyICFTextSegment(orgId, studyId, segmentId, input) {
        const response = await axios.put(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:studyId/draft/icf-text-segments/:segmentId`, {
            orgId,
            studyId,
            segmentId,
        }), input, authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(StudyICFTextSegmentDto, response.data);
    }
    async generateOrDownloadInformedConsent(studyId, orgId, studyVersionId, previewMetadata) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:studyId/:studyVersionId/preview-informed-consent`, {
            orgId,
            studyId,
            studyVersionId,
        }), { previewMetadata }, authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance((Array), response.data);
    }
    async getStudyReminder(orgId, studyId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:studyId/studyreminder`, { orgId, studyId }), authenticatedApiConfig(this.authenticationProvider));
        if (!response.data)
            return null;
        return plainToInstance(StudyReminderDto, response.data);
    }
    async upsertStudyReminder(orgId, studyId, input) {
        const response = await axios.put(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:studyId/studyreminder`, { orgId, studyId }), input, authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(StudyReminderDto, response.data);
    }
    async getStudyVersions(studyId, orgId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/studies/:studyId/versions`, { orgId, studyId }), authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(StudyVersionLiteDto, response.data);
    }
    studyVersionInputToPathParams(input) {
        if (input === StudyVersionStatus.DRAFT) {
            return "draft";
        }
        else if (input === StudyVersionStatus.ACTIVE) {
            return "active";
        }
        else if (input === "ACTIVE_FALLBACK_TO_DRAFT") {
            return "active-fallback-to-draft";
        }
        else {
            return input.studyVersionId;
        }
    }
    studyVersionTypeToPathParams(input) {
        if (input === StudyVersionStatus.DRAFT) {
            return "draft";
        }
        else if (input === StudyVersionStatus.ACTIVE) {
            return "active";
        }
        else if (input === "ACTIVE_FALLBACK_TO_DRAFT") {
            return "active-fallback-to-draft";
        }
        else {
            return willNeverHappen(input);
        }
    }
}
